/**
 * KAN-950: video tutorials registry for the cabinet.
 *
 * Data only: a react-router path template (+ optional query condition)
 * -> {slug, title, placement}. The video file is TUTORIALS_BASE_URL + slug + ".mp4".
 * The manager app (/m/*) is never served.
 * `section` groups the videos of one cabinet section: the button lists all of them.
 */
import { matchRoutes } from 'react-router-dom';
import { isMobileAppPath } from '@/lib/mobileApp';

/**
 * Where the floating button sits. "top-right" is for pages with an input
 * row or other controls at the bottom right (chat composer).
 */
export type TutorialPlacement = 'bottom-right' | 'top-right';

/** Cabinet sections (as in the side menu); the button offers every video of the section. */
export type TutorialSection =
  | 'dashboard'
  | 'crm'
  | 'leads'
  | 'contacts'
  | 'companies'
  | 'cold-calls'
  | 'orders'
  | 'catalog'
  | 'emails'
  | 'operators'
  | 'managers'
  | 'billing'
  | 'data-sources'
  | 'settings'
  | 'profile'
  | 'other';

export interface TutorialEntry {
  /** Route template as in react-router, e.g. "/clients/:id". */
  path: string;
  /**
   * Query parameters the URL must carry for this entry, e.g. {tab: "telegram"}.
   * Among entries of the same path the one with the most matched parameters wins;
   * an entry without query is the fallback of its path.
   */
  query?: Record<string, string>;
  /** Video file name without extension, latin letters, digits and hyphens. */
  slug: string;
  /** Title shown on the button and in the modal. */
  title: string;
  /** Button position, "bottom-right" when not set. */
  placement?: TutorialPlacement;
  /** Not shown on the button nor in the "Помощь" section (video to be re-recorded). */
  hidden?: boolean;
  /** Cabinet section; videos of one section are offered together on the button. */
  section?: TutorialSection;
  /**
   * Never the default video of a page (another entry of the same path and
   * query is), only offered in the section list of the button.
   */
  listOnly?: boolean;
}

export interface FoundTutorial extends TutorialEntry {
  placement: TutorialPlacement;
  videoUrl: string;
}

export const DEFAULT_TUTORIAL_PLACEMENT: TutorialPlacement = 'bottom-right';

/** Public bucket with the recorded videos; VITE_TUTORIALS_BASE_URL overrides it. */
export const DEFAULT_TUTORIALS_BASE_URL =
  'https://s3.ru1.storage.beget.cloud/d177523715c9-smeta-public/smeta-tutorials/';

export const TUTORIALS_BASE_URL: string =
  import.meta.env.VITE_TUTORIALS_BASE_URL || DEFAULT_TUTORIALS_BASE_URL;

export const TUTORIALS: TutorialEntry[] = [
  { path: '/', slug: 'dashboard', title: 'Главная', section: 'dashboard' },
  // KAN-952: no page of its own; opened by the "С чего начать" button and from the dashboard list.
  {
    path: '/',
    slug: 'onboarding',
    title: 'С чего начать: обзор кабинета и первая настройка',
    section: 'dashboard',
    listOnly: true,
  },
  { path: '/crm', slug: 'crm', title: 'CRM', section: 'crm' },
  { path: '/crm', query: { view: 'board' }, slug: 'crm-board', title: 'CRM: доска', section: 'crm' },
  { path: '/crm', query: { view: 'stats' }, slug: 'crm-stats', title: 'CRM: лиды по дням', section: 'crm' },
  {
    path: '/crm',
    query: { view: 'table', period: 'all' },
    slug: 'crm-card',
    title: 'CRM: строка клиента',
    section: 'crm',
  },
  { path: '/leads', slug: 'leads', title: 'Лиды', section: 'leads' },
  { path: '/clients', slug: 'clients', title: 'Контакты', section: 'contacts' },
  { path: '/clients/:id', slug: 'client-detail', title: 'Карточка клиента', section: 'contacts' },
  {
    path: '/clients/:clientId/chat',
    slug: 'client-chat',
    title: 'Переписка с клиентом',
    section: 'contacts',
    placement: 'top-right',
  },
  { path: '/companies', slug: 'companies', title: 'Организации', section: 'companies' },
  { path: '/companies/:id', slug: 'company-detail', title: 'Организация', section: 'companies' },
  { path: '/clients/cold-calls', slug: 'cold-calls', title: 'Холодные обзвоны', section: 'cold-calls' },
  { path: '/clients/cold-calls/:id', slug: 'dataset-detail', title: 'Список кампании', section: 'cold-calls' },
  {
    path: '/clients/cold-calls/:id/upload',
    slug: 'dataset-upload',
    title: 'Загрузка списка',
    section: 'cold-calls',
  },
  { path: '/orders', slug: 'orders', title: 'Заказы', section: 'orders' },
  { path: '/orders/:id', slug: 'order-detail', title: 'Заказ', section: 'orders' },
  { path: '/catalog', slug: 'catalog', title: 'Каталог', section: 'catalog' },
  { path: '/catalog/products/:id', slug: 'catalog-product', title: 'Товар', section: 'catalog' },
  { path: '/discounts', slug: 'discounts', title: 'Скидки', section: 'catalog' },
  { path: '/emails', slug: 'emails', title: 'Почтовый ящик', section: 'emails' },
  { path: '/users', slug: 'users', title: 'Пользователи', section: 'operators' },
  { path: '/users/:id', slug: 'user-detail', title: 'Страница оператора', section: 'operators' },
  {
    path: '/users/:id',
    query: { tab: 'instructions' },
    slug: 'operator-prompts',
    title: 'Оператор: инструкции',
    section: 'operators',
  },
  // Same tab as operator-prompts, which stays the default video; offered in the section list.
  {
    path: '/users/:id',
    query: { tab: 'instructions' },
    slug: 'operator-materials',
    title: 'Оператор: материалы',
    section: 'operators',
    listOnly: true,
  },
  {
    path: '/users/:id',
    query: { tab: 'greetings' },
    slug: 'operator-greetings-ab',
    title: 'Оператор: приветствия A/B',
    section: 'operators',
  },
  { path: '/managers', slug: 'managers', title: 'Менеджеры', section: 'managers' },
  { path: '/managers/:id', slug: 'manager-detail', title: 'Карточка менеджера', section: 'managers' },
  { path: '/billing', slug: 'billing', title: 'Баланс и расход', section: 'billing' },
  { path: '/expenses', slug: 'expenses', title: 'Расходы', section: 'billing' },
  { path: '/data-sources', slug: 'data-sources', title: 'Источники данных', section: 'data-sources' },
  { path: '/data-sources/:id', slug: 'data-source-detail', title: 'Источник данных', section: 'data-sources' },
  { path: '/settings', slug: 'settings-organization', title: 'Настройки', section: 'settings' },
  {
    path: '/settings',
    query: { tab: 'integrations' },
    slug: 'settings-integrations',
    title: 'Настройки: интеграции',
    section: 'settings',
  },
  { path: '/settings', query: { tab: 'funnel' }, slug: 'settings-funnel', title: 'Настройки: воронка', section: 'settings' },
  {
    path: '/settings',
    query: { tab: 'telephony' },
    slug: 'settings-telephony',
    title: 'Настройки: телефония',
    section: 'settings',
  },
  {
    path: '/settings',
    query: { tab: 'telegram' },
    slug: 'settings-telegram',
    title: 'Настройки: Telegram',
    section: 'settings',
  },
  { path: '/settings', query: { tab: 'max' }, slug: 'settings-max', title: 'Настройки: MAX', section: 'settings' },
  { path: '/settings', query: { tab: 'proxies' }, slug: 'settings-proxies', title: 'Настройки: прокси', section: 'settings' },
  { path: '/profile', slug: 'profile', title: 'Профиль', section: 'profile' },
  { path: '/services', slug: 'services', title: 'Услуги', hidden: true, section: 'other' },
  { path: '/merged-document', slug: 'merged-document', title: 'Итоговый документ', hidden: true, section: 'other' },
];

/** Slugs whose videos are hidden everywhere (button and "Помощь"). */
export function hiddenTutorialSlugs(registry: TutorialEntry[] = TUTORIALS): Set<string> {
  return new Set(registry.filter((entry) => entry.hidden).map((entry) => entry.slug));
}

/** KAN-952: video of the "С чего начать" button. */
export const ONBOARDING_TUTORIAL_SLUG = 'onboarding';

export function buildVideoUrl(slug: string, baseUrl: string = TUTORIALS_BASE_URL): string {
  return `${baseUrl.replace(/\/+$/, '')}/${slug}.mp4`;
}

/** Number of query conditions met, or -1 when any of them fails. */
function queryScore(entry: TutorialEntry, params: URLSearchParams): number {
  const conditions = Object.entries(entry.query ?? {});
  return conditions.every(([key, value]) => params.get(key) === value) ? conditions.length : -1;
}

/**
 * Finds the tutorial of the current page. `location` is a pathname, optionally
 * with "?query". Path matching is react-router's own (matchRoutes): static
 * segments outrank params, so "/clients/cold-calls" never falls into
 * "/clients/:id". Among entries of the matched path the most specific query
 * condition wins. Hidden and list-only entries are skipped.
 */
export function findTutorial(
  location: string,
  registry: TutorialEntry[] = TUTORIALS
): FoundTutorial | null {
  const queryAt = location.indexOf('?');
  const pathname = queryAt === -1 ? location : location.slice(0, queryAt);
  const params = new URLSearchParams(queryAt === -1 ? '' : location.slice(queryAt + 1));
  if (isMobileAppPath(pathname)) return null;

  const visible = registry.filter((entry) => !entry.hidden && !entry.listOnly);
  const paths = Array.from(new Set(visible.map((entry) => entry.path)));
  const matches = matchRoutes(
    paths.map((path) => ({ path })),
    pathname
  );
  const matchedPath = matches?.[matches.length - 1]?.route.path;
  if (matchedPath === undefined) return null;

  let best: TutorialEntry | null = null;
  let bestScore = -1;
  for (const entry of visible) {
    if (entry.path !== matchedPath) continue;
    const score = queryScore(entry, params);
    if (score > bestScore) {
      best = entry;
      bestScore = score;
    }
  }
  if (!best) return null;
  return {
    ...best,
    placement: best.placement ?? DEFAULT_TUTORIAL_PLACEMENT,
    videoUrl: buildVideoUrl(best.slug),
  };
}
